import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Music, Upload, Trash2, Loader2, CheckCircle2, AlertCircle, Play, Pause } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReelMusicManager = () => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [playing, setPlaying] = useState(null);
  const fileRef = useRef(null);
  const audioRef = useRef(null);

  const authHeader = () => {
    const t = localStorage.getItem('admin_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/reels/music`, { headers: authHeader() });
      setTracks(res.data.tracks || []);
    } catch (e) {
      toast({ title: 'Failed to load tracks', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const results = { ok: 0, fail: 0 };
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append('file', f);
      // Use the filename (sans extension, dashes->spaces, title-case) as default label
      const base = f.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ');
      fd.append('label', base.replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase()));
      try {
        await axios.post(`${API}/admin/reels/music`, fd, {
          headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
        });
        results.ok += 1;
      } catch (e) {
        console.error('upload failed:', f.name, e.response?.data);
        results.fail += 1;
      }
    }
    setUploading(false);
    if (results.ok > 0) toast({ title: `Uploaded ${results.ok} track${results.ok > 1 ? 's' : ''}` });
    if (results.fail > 0) toast({ title: `${results.fail} track(s) failed`, variant: 'destructive' });
    load();
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Delete "${filename}" permanently?`)) return;
    try {
      await axios.delete(`${API}/admin/reels/music/${encodeURIComponent(filename)}`, {
        headers: authHeader(),
      });
      toast({ title: 'Track deleted' });
      load();
    } catch (e) {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  const togglePlay = (filename) => {
    if (playing === filename) {
      audioRef.current?.pause();
      setPlaying(null);
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
    }
    const audio = new Audio(
      `${API}/admin/reels/music/${encodeURIComponent(filename)}/stream?token=${encodeURIComponent(localStorage.getItem('admin_token') || '')}`,
    );
    audioRef.current = audio;
    audio.onended = () => setPlaying(null);
    audio.play().catch(() => {
      // Fallback: try with admin password header via fetch → blob
      toast({ title: 'Cannot preview (check track validity)', variant: 'destructive' });
      setPlaying(null);
    });
    setPlaying(filename);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  };

  return (
    <Card data-testid="reel-music-manager">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Music className="w-5 h-5 text-blue-600" />
          Reels Music Library
        </CardTitle>
        <p className="text-sm text-gray-500 mt-1">
          Upload MP3/M4A tracks that dealers can pick when generating reels.
          You can replace the bundled placeholder tracks by uploading files with the same names:
          <span className="font-mono text-xs"> luxury.mp3, fast_sell.mp3, minimal_clean.mp3, new_arrival.mp3</span>.
        </p>
      </CardHeader>
      <CardContent>
        {/* Upload zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          data-testid="reel-music-upload-zone"
          className={`cursor-pointer border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
            dragOver
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
          }`}
        >
          {uploading ? (
            <Loader2 className="w-10 h-10 mx-auto mb-2 text-blue-600 animate-spin" />
          ) : (
            <Upload className="w-10 h-10 mx-auto mb-2 text-gray-400" />
          )}
          <p className="font-semibold text-gray-700">
            {uploading ? 'Uploading…' : 'Drop MP3/M4A files here or click to browse'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Multiple files supported · Max 12 MB per track · Loudness auto-normalised at render
          </p>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept="audio/mpeg,audio/mp3,audio/x-m4a,audio/mp4,audio/aac,audio/wav,.mp3,.m4a,.aac,.wav"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files)}
          />
        </div>

        {/* List */}
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-3">
            Available Tracks ({tracks.length})
          </h3>
          {loading ? (
            <p className="text-gray-500">Loading…</p>
          ) : tracks.length === 0 ? (
            <p className="text-gray-500">No tracks yet. Upload your first MP3.</p>
          ) : (
            <div className="space-y-2">
              {tracks.map((t) => (
                <div
                  key={t.filename}
                  data-testid={`music-track-${t.filename}`}
                  className="flex items-center gap-3 px-4 py-3 bg-white border border-gray-200 rounded-lg hover:border-blue-300 transition-colors"
                >
                  <button
                    onClick={() => togglePlay(t.filename)}
                    className="w-10 h-10 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center flex-shrink-0"
                    title="Preview"
                    data-testid={`music-play-${t.filename}`}
                  >
                    {playing === t.filename ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{t.label}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-2">
                      <span className="font-mono">{t.filename}</span>
                      <span>·</span>
                      <span>{(t.size / 1024).toFixed(0)} KB</span>
                      <span>·</span>
                      {t.source === 'uploaded' ? (
                        <span className="text-green-700 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Uploaded
                        </span>
                      ) : (
                        <span className="text-amber-700 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> Bundled placeholder
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(t.filename)}
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    data-testid={`music-delete-${t.filename}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ReelMusicManager;
